import { Link } from "react-router-dom";
import {
  PhoneCall, MessageSquare, Mail, MapPin, Clock, IndianRupee, Navigation, CalendarCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CLINIC } from "@/data/content";

const Contact = () => (
  <>
    <SEO
      title="Contact Dr. Ankur Lal | Delhi STD Clinic — Book a Confidential Consultation"
      description="Contact Delhi STD Clinic — call, WhatsApp or book a confidential consultation with Dr. Ankur Lal, STD & Sexual Health Specialist in Delhi."
      path="/contact"
    />
    <PageHero
      eyebrow="Contact"
      title="Contact Dr. Ankur Lal"
      intro="Reach the clinic directly — by phone, WhatsApp or the confidential booking form. Every enquiry is handled privately."
    />

    <section className="py-14 sm:py-20" data-testid="contact-details">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-8 items-start">
        <div className="space-y-4">
          <Reveal>
            <a
              href={`tel:${CLINIC.phoneRaw}`}
              data-testid="contact-phone-card"
              className="flex items-start gap-5 bg-white border border-slate-900/10 rounded-3xl p-6 hover:border-emerald-900/25 hover:shadow-md transition-all"
            >
              <span className="w-11 h-11 rounded-2xl bg-emerald-900 flex items-center justify-center shrink-0">
                <PhoneCall className="w-5 h-5 text-emerald-100" />
              </span>
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Phone</span>
                <span className="block mt-1 font-serif text-2xl font-semibold text-slate-950">{CLINIC.phoneDisplay}</span>
                <span className="block mt-1 text-sm text-slate-500">Tap to call the clinic directly</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.05}>
            <a
              href={CLINIC.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="flex items-start gap-5 bg-white border border-slate-900/10 rounded-3xl p-6 hover:border-emerald-900/25 hover:shadow-md transition-all"
            >
              <span className="w-11 h-11 rounded-2xl bg-emerald-700 flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-emerald-100" />
              </span>
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">WhatsApp</span>
                <span className="block mt-1 font-serif text-2xl font-semibold text-slate-950">{CLINIC.phoneDisplay}</span>
                <span className="block mt-1 text-sm text-slate-500">Message the clinic discreetly</span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-start gap-5 bg-white border border-dashed border-slate-300 rounded-3xl p-6" data-testid="contact-email-card">
              <span className="w-11 h-11 rounded-2xl bg-slate-200 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-slate-600" />
              </span>
              <span>
                <span className="block text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Email</span>
                <span className="block mt-1 text-base font-medium text-slate-700">{CLINIC.email}</span>
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-6" data-testid="contact-timings-card">
                <Clock className="w-5 h-5 text-emerald-900" />
                <p className="mt-3 text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Clinic Timings</p>
                <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{CLINIC.timings}</p>
              </div>
              <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-6" data-testid="contact-fees-card">
                <IndianRupee className="w-5 h-5 text-emerald-900" />
                <p className="mt-3 text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Consultation Fee</p>
                <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{CLINIC.fees}</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="bg-white border border-slate-900/10 rounded-3xl overflow-hidden h-full flex flex-col">
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-5">
                <span className="w-11 h-11 rounded-2xl bg-emerald-900 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-emerald-100" />
                </span>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Clinic Address</p>
                  <p className="mt-1.5 text-base font-medium text-slate-800">{CLINIC.address}</p>
                </div>
              </div>
            </div>
            <div
              className="flex-1 min-h-[260px] bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-900 flex flex-col items-center justify-center text-center p-8"
              data-testid="contact-map-placeholder"
            >
              <Navigation className="w-8 h-8 text-amber-400" />
              <p className="mt-4 font-serif text-2xl text-white">Google Maps location</p>
              <p className="mt-2 text-sm text-slate-400 max-w-xs">
                The interactive map will be embedded here once the clinic address is finalized.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 border border-emerald-300/40 text-emerald-100 text-sm font-semibold px-6 py-3 rounded-full cursor-not-allowed opacity-70">
                <Navigation className="w-4 h-4" /> Get Directions
              </span>
            </div>
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/booking?type=clinic"
                data-testid="contact-book-clinic"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-all"
              >
                <CalendarCheck className="w-4 h-4" /> Book Confidential Consultation
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
